import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { FaSave, FaCheckCircle, FaUpload, FaImage, FaShieldAlt, FaKey, FaMobileAlt, FaExclamationTriangle } from 'react-icons/fa';

const GROUPS = [
  {
    title: 'Şirket Bilgileri',
    fields: [
      { key: 'company_name', label: 'Şirket Adı', type: 'text' },
      { key: 'company_slogan', label: 'Slogan', type: 'text' },
      { key: 'company_phone', label: 'Telefon', type: 'text' },
      { key: 'company_email', label: 'E-posta', type: 'email' },
      { key: 'company_address', label: 'Adres', type: 'textarea' },
    ],
  },
  {
    title: 'İletişim & Sosyal Medya',
    fields: [
      { key: 'whatsapp_number', label: 'WhatsApp Numarası (ülke koduyla, örn: 905320000000)', type: 'text' },
      { key: 'map_embed_url', label: 'Harita Embed URL (OpenStreetMap)', type: 'text' },
      { key: 'facebook_url', label: 'Facebook URL', type: 'text' },
      { key: 'instagram_url', label: 'Instagram URL', type: 'text' },
      { key: 'linkedin_url', label: 'LinkedIn URL', type: 'text' },
    ],
  },
  {
    title: 'Ana Sayfa İçeriği',
    fields: [
      { key: 'hero_title', label: 'Ana Sayfa Başlığı (virgülden sonrası turuncu renkte görünür)', type: 'text' },
      { key: 'hero_subtitle', label: 'Ana Sayfa Alt Yazısı', type: 'textarea' },
      { key: 'about_text', label: '"Neden Biz?" Bölümü Metni', type: 'textarea' },
    ],
  },
  {
    title: 'Ana Sayfa İstatistikleri',
    fields: [
      { key: 'stat_experience', label: 'Deneyim Yılı (örn: 20+)', type: 'text' },
      { key: 'stat_products', label: 'Ürün Sayısı (örn: 500+)', type: 'text' },
      { key: 'stat_customers', label: 'Müşteri Sayısı (örn: 1000+)', type: 'text' },
      { key: 'stat_exports', label: 'İhracat Ülke Sayısı (örn: 50+)', type: 'text' },
    ],
  },
];

export default function AdminSettings() {
  const { token, login } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  // Site settings
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  // Credentials change
  const [credsForm, setCredsForm] = useState({ current_password: '', new_username: '', new_password: '', confirm_password: '' });
  const [credsSaving, setCredsSaving] = useState(false);
  const [credsMsg, setCredsMsg] = useState({ type: '', text: '' });

  // TOTP management
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [totpResetting, setTotpResetting] = useState(false);
  const [totpMsg, setTotpMsg] = useState('');

  useEffect(() => {
    axios.get('/api/settings').then(r => setSettings(r.data)).catch(() => {});
    axios.get('/api/auth/totp-status', { headers }).then(r => setTotpEnabled(r.data.totp_enabled)).catch(() => {});
  }, []);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoUploading(true);
    const fd = new FormData(); fd.append('image', file);
    try {
      const { data } = await axios.post('/api/upload', fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setSettings(s => ({ ...s, logo_url: data.url }));
    } catch { alert('Yükleme başarısız'); }
    setLogoUploading(false);
    e.target.value = '';
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put('/api/settings', settings, { headers });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {}
    setSaving(false);
  };

  const handleCredsChange = async () => {
    if (!credsForm.current_password) return setCredsMsg({ type: 'error', text: 'Mevcut şifre gerekli' });
    if (!credsForm.new_username.trim() && !credsForm.new_password.trim()) {
      return setCredsMsg({ type: 'error', text: 'Yeni kullanıcı adı veya şifre giriniz' });
    }
    if (credsForm.new_password && credsForm.new_password !== credsForm.confirm_password) {
      return setCredsMsg({ type: 'error', text: 'Yeni şifreler eşleşmiyor' });
    }
    setCredsSaving(true); setCredsMsg({ type: '', text: '' });
    try {
      const payload = { current_password: credsForm.current_password };
      if (credsForm.new_username.trim()) payload.new_username = credsForm.new_username.trim();
      if (credsForm.new_password.trim()) payload.new_password = credsForm.new_password.trim();
      const { data } = await axios.put('/api/auth/change-credentials', payload, { headers });
      login(data.token, data.username);
      setCredsMsg({ type: 'success', text: 'Güncellendi! Yeni bilgilerle devam edebilirsiniz.' });
      setCredsForm({ current_password: '', new_username: '', new_password: '', confirm_password: '' });
      setTimeout(() => setCredsMsg({ type: '', text: '' }), 4000);
    } catch (err) {
      setCredsMsg({ type: 'error', text: err.response?.data?.message || 'Hata oluştu' });
    }
    setCredsSaving(false);
  };

  const handleTotpReset = async () => {
    if (!window.confirm('TOTP sıfırlanacak. Bir sonraki girişte Google Authenticator yeniden kurulumu yapılması gerekecek. Onaylıyor musunuz?')) return;
    setTotpResetting(true); setTotpMsg('');
    try {
      await axios.post('/api/auth/reset-totp', {}, { headers });
      setTotpEnabled(false);
      setTotpMsg('TOTP sıfırlandı. Bir sonraki girişte yeniden kurulum yapılacak.');
    } catch {
      setTotpMsg('Sıfırlama başarısız, tekrar deneyin.');
    }
    setTotpResetting(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary">Site Ayarları</h1>
          <p className="text-gray-500 text-sm">Şirket bilgileri ve genel ayarlar</p>
        </div>
        <button onClick={handleSave} disabled={saving}
          className={`btn-primary text-sm py-2.5 disabled:opacity-60 ${saved ? 'bg-green-500 hover:bg-green-600' : ''}`}>
          {saved ? <><FaCheckCircle /> Kaydedildi!</> : saving ? 'Kaydediliyor...' : <><FaSave /> Kaydet</>}
        </button>
      </div>

      {/* Logo Yönetimi */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-base font-semibold text-primary mb-4 pb-2 border-b border-gray-100">Logo Yönetimi</h2>
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          <div className="flex-shrink-0 w-48 h-24 rounded-xl bg-primary flex items-center justify-center border border-gray-200 overflow-hidden">
            {settings.logo_url ? (
              <img src={settings.logo_url} alt="Logo"
                style={{ height: `${settings.logo_height || 40}px`, maxWidth: '160px' }}
                className="object-contain" />
            ) : (
              <div className="flex flex-col items-center gap-1">
                <FaImage size={28} className="text-white/30" />
                <span className="text-white/40 text-xs">Önizleme</span>
              </div>
            )}
          </div>
          <div className="flex-1 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Logo Dosyası</label>
              <label className="btn-secondary text-sm py-2.5 cursor-pointer inline-flex">
                {logoUploading ? 'Yükleniyor...' : <><FaUpload size={13} /> Bilgisayardan Seç</>}
                <input type="file" accept="image/*,.svg" onChange={handleLogoUpload} className="hidden" disabled={logoUploading} />
              </label>
              {settings.logo_url && <p className="text-xs text-gray-400 mt-1 truncate max-w-xs">{settings.logo_url}</p>}
            </div>
            <div className="w-40">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Logo Yüksekliği (px)</label>
              <input type="number" min={20} max={120} value={settings.logo_height || 40}
                onChange={e => setSettings(s => ({ ...s, logo_height: e.target.value }))}
                className="input-field" placeholder="40" />
            </div>
          </div>
        </div>
      </div>

      {GROUPS.map(group => (
        <div key={group.title} className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-base font-semibold text-primary mb-4 pb-2 border-b border-gray-100">{group.title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {group.fields.map(f => (
              <div key={f.key} className={f.type === 'textarea' ? 'md:col-span-2' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{f.label}</label>
                {f.type === 'textarea' ? (
                  <textarea value={settings[f.key] || ''} onChange={e => setSettings(s => ({ ...s, [f.key]: e.target.value }))}
                    rows={3} className="input-field resize-none" />
                ) : (
                  <input type={f.type} value={settings[f.key] || ''} onChange={e => setSettings(s => ({ ...s, [f.key]: e.target.value }))}
                    className="input-field" />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="bg-white rounded-xl shadow-sm p-6">
        <button onClick={handleSave} disabled={saving}
          className={`btn-primary disabled:opacity-60 ${saved ? 'bg-green-500 hover:bg-green-600' : ''}`}>
          {saved ? <><FaCheckCircle /> Kaydedildi!</> : saving ? 'Kaydediliyor...' : <><FaSave /> Tüm Ayarları Kaydet</>}
        </button>
      </div>

      {/* Hesap Güvenliği */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-base font-semibold text-primary mb-1 flex items-center gap-2">
          <FaKey className="text-secondary" size={15} /> Hesap Güvenliği
        </h2>
        <p className="text-gray-400 text-sm mb-5">Kullanıcı adı veya şifreni değiştirmek için mevcut şifreni gir.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Mevcut Şifre *</label>
            <input type="password" value={credsForm.current_password}
              onChange={e => setCredsForm(f => ({ ...f, current_password: e.target.value }))}
              className="input-field" placeholder="••••••••" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Yeni Kullanıcı Adı
              <span className="text-gray-400 font-normal ml-1">(değiştirmeyeceksen boş bırak)</span>
            </label>
            <input type="text" value={credsForm.new_username}
              onChange={e => setCredsForm(f => ({ ...f, new_username: e.target.value }))}
              className="input-field" placeholder="yeni-kullanici-adi" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Yeni Şifre
              <span className="text-gray-400 font-normal ml-1">(değiştirmeyeceksen boş bırak)</span>
            </label>
            <input type="password" value={credsForm.new_password}
              onChange={e => setCredsForm(f => ({ ...f, new_password: e.target.value }))}
              className="input-field" placeholder="••••••••" />
          </div>
          {credsForm.new_password && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Yeni Şifre (Tekrar)</label>
              <input type="password" value={credsForm.confirm_password}
                onChange={e => setCredsForm(f => ({ ...f, confirm_password: e.target.value }))}
                className="input-field" placeholder="••••••••" />
            </div>
          )}
        </div>
        {credsMsg.text && (
          <div className={`mt-4 rounded-lg px-4 py-2.5 text-sm max-w-2xl ${credsMsg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
            {credsMsg.text}
          </div>
        )}
        <button onClick={handleCredsChange} disabled={credsSaving}
          className="mt-4 btn-primary text-sm py-2.5 disabled:opacity-60">
          {credsSaving ? 'Güncelleniyor...' : <><FaKey size={13} /> Bilgileri Güncelle</>}
        </button>
      </div>

      {/* İki Faktörlü Doğrulama */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-base font-semibold text-primary mb-1 flex items-center gap-2">
          <FaMobileAlt className="text-secondary" size={15} /> İki Faktörlü Doğrulama (2FA)
        </h2>
        <p className="text-gray-400 text-sm mb-5">Google Authenticator ile giriş güvenliğini yönet.</p>

        <div className="flex items-center gap-3 mb-5">
          <span className={`px-3 py-1.5 rounded-full text-sm font-medium inline-flex items-center gap-1.5 ${totpEnabled ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
            <FaShieldAlt size={11} />
            {totpEnabled ? 'Aktif — 2FA koruması açık' : 'Kurulmamış — bir sonraki girişte kurulum yapılacak'}
          </span>
        </div>

        {totpEnabled && (
          <div className="border border-orange-200 bg-orange-50 rounded-xl p-4 max-w-xl">
            <div className="flex gap-3">
              <FaExclamationTriangle className="text-orange-500 flex-shrink-0 mt-0.5" size={16} />
              <div>
                <p className="text-sm font-medium text-orange-800 mb-1">TOTP'u sıfırla</p>
                <p className="text-sm text-orange-700 mb-3">
                  Siteyi başka birine devrediyorsanız TOTP'u sıfırlayın. Bir sonraki girişte yeni kişi kendi Google Authenticator'ını kurabilir.
                </p>
                <button onClick={handleTotpReset} disabled={totpResetting}
                  className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-60">
                  {totpResetting ? 'Sıfırlanıyor...' : 'TOTP\'u Sıfırla'}
                </button>
              </div>
            </div>
          </div>
        )}

        {totpMsg && (
          <div className="mt-4 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg px-4 py-2.5 text-sm max-w-xl">
            {totpMsg}
          </div>
        )}
      </div>
    </div>
  );
}
